// Member detail — demographics (admin), Stripe + Healthie IDs, membership subscription + payment
// history. Membership billing only; all clinical billing lives in Healthie (D5/D8).
import { Link, useParams } from "react-router-dom";
import { useApi } from "../api";
import { useAsync } from "../lib/useAsync";
import { cap, formatDateTime, formatMoney } from "../lib/format";
import { Loading, ErrorState, EmptyState, PageHeader, StatusBadge } from "../components/ui";
import { MemberActions } from "../components/MemberActions";

function initials(a?: string, b?: string) {
  return `${(a || "M").charAt(0)}${(b || "").charAt(0)}`.toUpperCase();
}

export function MemberDetail() {
  const { id = "" } = useParams();
  const api = useApi();
  const { data, loading, error, reload } = useAsync(() => api.member(id), [id]);

  // Full-page loader only on the INITIAL load. On reload() after a write, keep the content + actions
  // panel mounted so the success message and inputs aren't wiped.
  if (loading && !data) return <Loading />;
  if (error && !data) return <ErrorState message={error} />;
  if (!data) return <ErrorState message="Member not found." />;
  const m = data.member;

  return (
    <div className="page cmd-detail">
      <PageHeader title={`${m.first_name} ${m.last_name}`} sub={m.email} />
      <Link to="/members" className="back-link">
        ← All members
      </Link>

      {/* Identity header + read-only admin facts */}
      <div className="settings-card detail-identity">
        <div className="detail-identity__head">
          <div className="detail-avatar">{initials(m.first_name, m.last_name)}</div>
          <div>
            <div className="detail-name">{m.first_name} {m.last_name}</div>
            <div className="detail-email">{m.email}</div>
            <div className="detail-tags">
              <span className="tag">{cap(m.tier)}</span>
              <StatusBadge value={m.status} />
            </div>
          </div>
        </div>
      </div>

      <div className="settings-card">
        <div className="pcard-head"><h3>Account <em>details</em></h3></div>
        <dl className="kv">
          <div><dt>Phone</dt><dd>{m.phone ?? "—"}</dd></div>
          <div><dt>Member since</dt><dd>{formatDateTime(m.created_at)}</dd></div>
          <div><dt>Stripe customer</dt><dd className="mono">{m.stripe_customer_id ?? "—"}</dd></div>
          <div><dt>Stripe subscription</dt><dd className="mono">{m.stripe_subscription_id ?? "—"}</dd></div>
          <div><dt>Healthie patient</dt><dd className="mono">{m.healthie_patient_id ?? "—"}</dd></div>
        </dl>
      </div>

      <MemberActions member={{ ...m, id }} onChanged={reload} />

      <div className="settings-card">
        <div className="pcard-head"><h3>Membership</h3></div>
        {data.subscription ? (
          <p>
            {cap(data.subscription.product)} · <StatusBadge value={data.subscription.status} /> ·
            renews {formatDateTime(data.subscription.current_period_end)}
          </p>
        ) : (
          <EmptyState>No subscription.</EmptyState>
        )}
      </div>

      <div className="settings-card">
        <div className="pcard-head"><h3>Payment <em>history</em></h3></div>
        {data.payments.length ? (
          <table className="tbl">
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.payments.map((p, i) => (
                <tr key={i}>
                  <td className="mono">{formatDateTime(p.created_at)}</td>
                  <td>{formatMoney(p.amount_cents, p.currency)}</td>
                  <td>
                    <StatusBadge value={p.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState>No payments.</EmptyState>
        )}
      </div>
    </div>
  );
}
